/**
 * How far through a set, as the MyRave ICON (the user, 2026-10-30: "the progress
 * bar is built from the MyRave icon … extend it all the way to the end of the line;
 * animated bars go up and down; the past is the animated bars in 2–3 colours from
 * the stage's colours; the future muted bars, but visible").
 *
 * - **Each group is the icon**: the mark's five rounded bars at its own heights
 *   (46 · 90 · 62 · 90 · 46, `LogoMark`) and its own widths and gaps — repeated
 *   side by side to the end of the line.
 * - **The past moves, in the stage's colours**: the icon's three tones — three solid
 *   tones AROUND the stage's colour — a deeper one, the colour itself, a lighter one — each bar on the logo meter's own beat
 *   (`myrave-meter`, tokens.css), slowed, each icon a little behind the last.
 * - **The future is low, even bars, muted and still** — "just empty bars" (the user).
 * - **The boundary is exact**: the moving layer is CLIPPED at `--p`, the set's real
 *   progress from its (shifted) start and end — never rounded to a bar.
 * Nothing moves under reduced motion.
 */
import { memo, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import './LiveWave.css';

/** How many icons fill the line before it is measured (a phone's card). */
export const WAVE_ICONS = 8;
/** One bar and the gap after it, in px: the icon's bars stay this thick at every width — a wider line holds more icons. */
const BAR_PITCH = 6;
/** Icons that fill a line this wide, end to end. */
export function iconsFor(width: number): number {
  return Math.max(3, Math.round((width + 2) / (BAR_PITCH * 5)));
}
/** The icon's bars, as shares of the tallest (`LogoMark`'s small drawing: 46 · 90 · 62 · 90 · 46). */
export const ICON_BARS = [46 / 90, 1, 62 / 90, 1, 46 / 90] as const;
/** Every bar on a line not measured yet. */
export const WAVE_BARS = WAVE_ICONS * ICON_BARS.length;

/** How far through, 0–100, from a set's start and end — never outside them. */
export function waveProgress(startMs: number, endMs: number, now: number): number {
  if (!(endMs > startMs)) return now >= endMs ? 100 : 0;
  return Math.max(0, Math.min(100, ((now - startMs) / (endMs - startMs)) * 100));
}

/**
 * The played bars take turns (the user, 2026-10-30): up and down (the menu button's meter), a
 * wave left to right, all breathing together, the meter, a wave out from the middle, a twinkle,
 * the meter, a wave right to left — and round again. From one kind to
 * the next each bar EASES from where it is into the new movement (`BLEND_MS`), never cut.
 * ONE animation frame loop for every waveform on the page, writing each played bar's `transform`
 * — no React render; it rests while the page is hidden and never runs under reduced motion.
 */
export const WAVE_PHASES = [
  ['meter', 4000], ['ltr', 7600], ['pulse', 4000], ['meter', 4000], ['center', 7600], ['twinkle', 4000], ['meter', 4000], ['rtl', 7600],
] as const;
type Phase = (typeof WAVE_PHASES)[number][0];
const CYCLE_MS = WAVE_PHASES.reduce((t, [, ms]) => t + ms, 0);
const BLEND_MS = 500;
const LOW = 0.42;
/** One speed for every bar in every style (the user): a full rise and fall takes this long. */
const PERIOD_MS = 1300;
/** The meter: the menu button's five bars, each a different moment of the same beat. */
const METER_OFFSET = [0.1, 0.55, 0.3, 0.8, 0.45] as const;

const swing = (x: number) => 0.5 - 0.5 * Math.cos(2 * Math.PI * x);
/** A bar's height (as a scale) in one kind of movement, at time `t`. */
export function barScale(kind: Phase, i: number, n: number, t: number): number {
  if (kind === 'meter') return 1 - (1 - LOW) * swing(t / PERIOD_MS + METER_OFFSET[i % 5]! + Math.floor(i / 5) * 0.07);
  if (kind === 'pulse') return LOW + (1 - LOW) * swing(t / PERIOD_MS + 0.5);
  if (kind === 'twinkle') return LOW + (1 - LOW) * swing(t / PERIOD_MS + (((i * 2654435761) >>> 0) % 1000) / 1000);
  const at = kind === 'ltr' ? i : kind === 'rtl' ? n - 1 - i : Math.abs(i - (n - 1) / 2);
  return LOW + (1 - LOW) * swing((t - at * 30) / PERIOD_MS);
}
/** Which kind is on at `t`, how long it has been on, and the one before it. */
function phaseAt(t: number): { kind: Phase; since: number; before: Phase } {
  let at = t % CYCLE_MS;
  for (let k = 0; k < WAVE_PHASES.length; k++) {
    const [kind, ms] = WAVE_PHASES[k]!;
    if (at < ms) return { kind, since: at, before: WAVE_PHASES[(k + WAVE_PHASES.length - 1) % WAVE_PHASES.length]![0] };
    at -= ms;
  }
  return { kind: 'meter', since: 0, before: 'rtl' };
}

const lines = new Map<HTMLElement, { bars: HTMLElement[]; n: number }>();
let frame = 0;
function tick(now: number): void {
  const { kind, since, before } = phaseAt(now);
  const k = since < BLEND_MS ? swing(since / BLEND_MS / 2) : 1;
  for (const { bars, n } of lines.values()) {
    bars.forEach((bar, i) => {
      const v = k < 1 ? barScale(before, i, n, now) * (1 - k) + barScale(kind, i, n, now) * k : barScale(kind, i, n, now);
      bar.style.transform = `scaleY(${v.toFixed(3)})`;
    });
  }
  frame = requestAnimationFrame(tick);
}
const stillWanted = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
function follow(el: HTMLElement, n: number): () => void {
  lines.set(el, { bars: [...el.querySelectorAll<HTMLElement>('i[data-on]')], n });
  if (!frame && !stillWanted() && typeof requestAnimationFrame === 'function') frame = requestAnimationFrame(tick);
  return () => {
    lines.delete(el);
    if (lines.size === 0 && frame) { cancelAnimationFrame(frame); frame = 0; }
  };
}

function Icons({ count, lit }: { count: number; lit?: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, g) => (
        <span key={g} className="live-wave-icon" style={{ '--g': g } as CSSProperties}>
          {ICON_BARS.map((h, b) => (
            // The future is low, even bars (the user): only the played icons stand at the icon's heights.
            <i key={b} data-on={lit !== undefined && g * ICON_BARS.length + b < lit ? '' : undefined} style={lit === undefined ? undefined : { height: `${Math.round(h * 100)}%`, '--i': g * ICON_BARS.length + b } as CSSProperties} />
          ))}
        </span>
      ))}
    </>
  );
}

export const LiveWave = memo(function LiveWave({ pct, label }: {
  /** Kept for the callers: every stage draws the same icon. */
  seed?: string;
  /** How far through, 0–100. */
  pct: number;
  /** What the bar says to a screen reader — "Elapsed: 30m (33%)". */
  label: string;
}) {
  const box = useRef<HTMLSpanElement>(null);
  const [icons, setIcons] = useState(WAVE_ICONS);
  const done = useRef<HTMLSpanElement>(null);
  // As many icons as the line is wide — measured, and again when the card changes size.
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const fit = () => { const w = el.clientWidth; if (w > 0) setIcons(iconsFor(w)); };
    fit();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const bars = icons * ICON_BARS.length;
  // Bars past the clip never show: they need not move either.
  const lit = Math.min(bars, Math.ceil((pct / 100) * bars));
  const p = Math.round(pct * 10) / 10;
  // The played bars join the page's one animation loop — again whenever how many are played changes.
  useEffect(() => (done.current ? follow(done.current, bars) : undefined), [lit, bars]);
  return (
    <span
      ref={box} className="live-wave" role="progressbar" aria-label={label}
      aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)}
      style={{ '--p': `${p}%`, '--n': icons * ICON_BARS.length } as CSSProperties}
    >
      <span className="live-wave-rest" aria-hidden="true"><Icons count={icons} /></span>
      <span ref={done} className="live-wave-done" aria-hidden="true"><Icons count={icons} lit={lit} /></span>
    </span>
  );
});
